import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { X, Database, HardDrive, Activity, Users as UsersIcon, Mail, GitBranch, Server, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";

// 2026-02-27 (Dave) — Database Dashboard (real).
//
// Read-only system-health glance panel for Platform Admins. Pulls from
// GET /api/admin/db-dashboard, which is Postgres-only (no extra Supabase
// permissions). Cached 60 s server-side; auto-refresh every 60 s
// client-side. Manual refresh button bypasses the cache (`?refresh=1`).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REFRESH_MS = 60_000;

export default function DatabaseDashboardModal({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  const fetchData = useCallback(async (bust = false) => {
    try {
      setError("");
      if (bust) setRefreshing(true);
      const res = await axios.get(
        `${API}/admin/db-dashboard${bust ? "?refresh=1" : ""}`,
      );
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(false);
    timerRef.current = setInterval(() => fetchData(false), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cacheAge = data?.cache_age_seconds ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.35)" }}
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl border-2 border-navy w-[min(1400px,96vw)] h-[min(720px,92vh)] flex flex-col rounded-[8px]"
        data-testid="db-dashboard-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b-2 border-navy"
          style={{ background: "#F7FAFC" }}
        >
          <div>
            <div className="text-[15px] font-bold text-navy tracking-[0.06em] flex items-center gap-2">
              <Database size={16} strokeWidth={2} />
              Database Dashboard
            </div>
            <div className="text-[11.5px] text-slate mt-1">
              System health at a glance · Read-only · Refreshed automatically every 60 seconds
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy text-navy text-[11.5px] font-semibold rounded-[3px] hover:bg-navy hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="db-dashboard-refresh"
              title="Force-refresh (bypass 60 s server cache)"
            >
              {refreshing
                ? <Loader2 size={12} className="animate-spin" />
                : <RefreshCw size={12} />}
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate hover:text-coral transition-colors"
              data-testid="db-dashboard-close"
              title="Close (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading && !data ? (
            <div className="flex items-center justify-center h-full text-[11.5px] text-slate italic">
              <Loader2 size={14} className="animate-spin mr-2" />
              Loading dashboard…
            </div>
          ) : error && !data ? (
            <div className="flex items-center justify-center h-full text-[12px] text-coral">
              <AlertTriangle size={14} className="mr-2" />
              {error}
            </div>
          ) : (
            /* 2026-02-27 (Dave · v2) — 4 × 2 grid so every card fits
               inside the fixed modal shell without scrolling. Row 1
               groups the "state of the DB" (Storage, Row Counts,
               Connections, Activity); Row 2 groups the "state of the
               app" (Email, Sessions, Schema, Environment). */
            <div className="grid grid-cols-4 gap-3">
              <StorageCard payload={data?.storage} />
              <RowCountsCard payload={data?.row_counts} />
              <ConnectionsCard payload={data?.connections} />
              <ActivityCard payload={data?.activity_24h} />

              <EmailHealthCard payload={data?.email_health_24h} />
              <SessionsCard payload={data?.sessions_now} />
              <SchemaCard payload={data?.schema} />
              <EnvironmentCard payload={data?.environment} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 border-t border-rule flex items-center justify-between text-[11px] text-slate"
          style={{ background: "#F7FAFC" }}
        >
          <span data-testid="db-dashboard-generated-at">
            Generated at{" "}
            {data?.generated_at
              ? new Date(data.generated_at).toLocaleString(undefined, {
                  timeZone: "America/Chicago",
                  hour: "numeric", minute: "2-digit", second: "2-digit",
                })
              : "—"}
          </span>
          <span className="font-mono tabular-nums">
            Cache age: {cacheAge}s · auto-refresh 60 s
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Reusable pieces ---------------------------------------- */

function Card({ icon: Icon, title, children, source, error }) {
  return (
    <div className="border border-rule rounded-[6px] p-3 bg-white flex flex-col">
      <div
        className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-2 pb-1 flex items-center gap-2"
        style={{ borderBottom: "1px solid #EA6A47" }}
      >
        {Icon && <Icon size={12} strokeWidth={2.2} />}
        {title}
      </div>
      <div className="flex-1">
        {error ? (
          <div className="text-[11px] text-coral leading-snug break-words">
            <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />
            {error}
          </div>
        ) : children}
      </div>
      {source && (
        <div className="mt-2 pt-1.5 border-t border-rule text-[9.5px] tracking-[0.06em] font-mono text-slate uppercase">
          {source}
        </div>
      )}
    </div>
  );
}

function BigNumber({ value, unit, tone = "navy" }) {
  const color = tone === "coral" ? "#EA6A47" : "#1C4E80";
  return (
    <div className="flex items-baseline gap-1.5">
      <span
        className="font-mono tabular-nums font-bold"
        style={{ fontSize: 26, color, lineHeight: 1 }}
      >
        {value}
      </span>
      {unit && <span className="text-[11px] text-slate">{unit}</span>}
    </div>
  );
}

function KV({ k, v, tone }) {
  return (
    <div className="flex items-center justify-between text-[11.5px] py-0.5">
      <span className="text-slate">{k}</span>
      <span
        className={
          "font-mono tabular-nums font-semibold " +
          (tone === "coral" ? "text-coral" : "text-ink")
        }
      >
        {v}
      </span>
    </div>
  );
}

/* Byte formatter — humanizes 47300000 → "45.1 MB", 4200 → "4.1 KB". */
function fmtBytes(n) {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function fmtInt(n) {
  if (n === -1 || n == null) return "—";
  return Number(n).toLocaleString();
}

function fmtUptime(s) {
  if (!Number.isFinite(s)) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h >= 1) return `${h} h ${m} m`;
  if (m >= 1) return `${m} m`;
  return `${s} s`;
}

/* ---------- Row 1: Storage · Row Counts · Connections -------------- */

function StorageCard({ payload }) {
  if (payload?.error) return <Card icon={HardDrive} title="Storage" error={payload.error} source="pg_database_size · element_attachments" />;
  const p = payload || {};
  const dbUsed = p.total_bytes ?? 0;
  const dbQuota = p.db_quota_bytes ?? 0;
  const filesUsed = p.files_bytes ?? 0;
  const filesCount = p.files_count ?? 0;
  const filesQuota = p.files_quota_bytes ?? 0;
  const pct = (used, quota) => (quota > 0 ? Math.min(100, (used / quota) * 100) : 0);
  return (
    <Card icon={HardDrive} title="Storage" source="pg_database_size · element_attachments">
      {/* Database */}
      <div className="text-[10px] tracking-[0.08em] uppercase text-slate font-semibold">
        Database
      </div>
      <div className="flex items-baseline justify-between mt-0.5">
        <span className="font-mono tabular-nums font-bold text-navy text-[18px] leading-none">
          {fmtBytes(dbUsed)}
        </span>
        <span className="text-[10.5px] text-slate">
          of {fmtBytes(dbQuota)} plan
        </span>
      </div>
      <div className="h-1 bg-[#EAEEF3] rounded-full mt-1 overflow-hidden">
        <div
          className="h-full bg-navy"
          style={{ width: `${pct(dbUsed, dbQuota)}%` }}
        />
      </div>
      {/* File uploads */}
      <div className="text-[10px] tracking-[0.08em] uppercase text-slate font-semibold mt-2.5">
        File Uploads
      </div>
      <div className="flex items-baseline justify-between mt-0.5">
        <span className="font-mono tabular-nums font-bold text-navy text-[18px] leading-none">
          {fmtBytes(filesUsed)}
        </span>
        <span className="text-[10.5px] text-slate">
          of {fmtBytes(filesQuota)} plan · {fmtInt(filesCount)} files
        </span>
      </div>
      <div className="h-1 bg-[#EAEEF3] rounded-full mt-1 overflow-hidden">
        <div
          className="h-full bg-coral"
          style={{ width: `${pct(filesUsed, filesQuota)}%` }}
        />
      </div>
      {/* Top tables */}
      <div className="text-[10px] tracking-[0.08em] uppercase text-slate font-semibold mt-2.5 mb-0.5">
        Top DB tables
      </div>
      {(p.top_tables || []).slice(0, 3).map((t) => (
        <KV key={t.name} k={t.name} v={fmtBytes(t.bytes)} />
      ))}
    </Card>
  );
}

function RowCountsCard({ payload }) {
  if (payload?.error) return <Card icon={Database} title="Row Counts" error={payload.error} />;
  const p = payload || {};
  return (
    <Card icon={Database} title="Row Counts" source="SELECT count(*) — cached 60 s">
      <KV k="Firms" v={fmtInt(p.firms)} />
      <KV k="Associations" v={fmtInt(p.associations)} />
      <KV k="Users" v={fmtInt(p.users)} />
      <KV k="Elements" v={fmtInt(p.elements)} />
      <KV k="Plan Years" v={fmtInt(p.plan_years)} />
      <KV k="Scenarios" v={fmtInt(p.scenarios)} />
      <KV k="Audit rows" v={fmtInt(p.audit_rows)} />
      <KV k="Email log rows" v={fmtInt(p.email_log)} />
    </Card>
  );
}

function ConnectionsCard({ payload }) {
  if (payload?.error) return <Card icon={Activity} title="Active Connections" error={payload.error} />;
  const p = payload || {};
  return (
    <Card icon={Activity} title="Active Connections" source="pg_stat_activity">
      <BigNumber value={fmtInt(p.total)} unit={`/ ${fmtInt(p.max_connections)} pool`} />
      <div className="mt-3 space-y-0.5">
        <KV k="Active queries" v={fmtInt(p.active)} />
        <KV k="Idle" v={fmtInt(p.idle)} />
        <KV k="Idle in txn" v={fmtInt(p.idle_in_txn)} />
      </div>
    </Card>
  );
}

/* ---------- Row 2: Activity · Email · Sessions --------------------- */

const ACTION_LABELS = {
  create: "Records created",
  update: "Records updated",
  delete: "Records deleted",
  sent: "Emails sent",
  invite_sent: "Invites sent",
  change_own_password: "Password changes",
  acknowledge_sop: "SOP acknowledgements",
  password_reset: "Password resets",
  deactivate: "Users deactivated",
  reactivate: "Users reactivated",
  login: "Logins",
};

function ActivityCard({ payload }) {
  if (payload?.error) return <Card icon={Activity} title="Activity — Last 24 h" error={payload.error} />;
  const p = payload || {};
  const rows = Object.entries(p.by_action || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  return (
    <Card icon={Activity} title="Activity — Last 24 h" source="audit_events">
      <BigNumber value={fmtInt(p.total)} unit="events" />
      <div className="mt-3 space-y-0.5">
        <KV k="Unique actors" v={fmtInt(p.unique_actors)} />
        {rows.map(([action, n]) => (
          <KV key={action} k={ACTION_LABELS[action] || action} v={fmtInt(n)} />
        ))}
      </div>
    </Card>
  );
}

function EmailHealthCard({ payload }) {
  if (payload?.error) return <Card icon={Mail} title="Email Health — Last 24 h" error={payload.error} />;
  const p = payload || {};
  return (
    <Card icon={Mail} title="Email Health — Last 24 h" source="email_log">
      <div className="grid grid-cols-2 gap-2">
        <BigNumber value={fmtInt(p.sent)} unit="sent" />
        <BigNumber
          value={`${p.bounce_pct ?? 0}%`}
          unit="bounce"
          tone={Number(p.bounce_pct) > 2 ? "coral" : "navy"}
        />
      </div>
      <div className="mt-3 space-y-0.5">
        <KV k="Delivered" v={fmtInt(p.delivered)} />
        <KV k="Bounced" v={fmtInt(p.bounced)} tone={p.bounced > 0 ? "coral" : undefined} />
        <KV k="Inbound replies" v={fmtInt(p.inbound_replies)} />
      </div>
    </Card>
  );
}

function SessionsCard({ payload }) {
  if (payload?.error) return <Card icon={UsersIcon} title="Sessions Right Now" error={payload.error} />;
  const p = payload || {};
  const roleLabel = { administrator: "Administrators", analyst: "Analysts", view_only: "View-Only" };
  return (
    <Card icon={UsersIcon} title="Sessions Right Now" source="users.last_seen_at ≤ 15 min">
      <BigNumber value={fmtInt(p.signed_in)} unit="signed in" />
      <div className="mt-3 space-y-0.5">
        {Object.entries(p.by_role || {}).map(([role, n]) => (
          <KV key={role} k={roleLabel[role] || role} v={fmtInt(n)} />
        ))}
        {(!p.by_role || Object.keys(p.by_role).length === 0) && (
          <div className="text-[11px] text-slate italic mt-2">
            No live sessions in the last 15 minutes.
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------- Row 3: Schema · Environment ---------------------------- */

function SchemaCard({ payload }) {
  if (payload?.error) return <Card icon={GitBranch} title="Schema" error={payload.error} />;
  const p = payload || {};
  return (
    <Card icon={GitBranch} title="Schema" source="alembic_version · information_schema">
      <div className="text-[10.5px] tracking-[0.08em] uppercase text-slate font-semibold">
        Current Migration
      </div>
      <div className="font-mono text-[12px] text-ink mt-1 break-all">
        {p.migration_head || "—"}
      </div>
      <div className="mt-3 space-y-0.5">
        <KV k="Tables (public)" v={fmtInt(p.tables)} />
        <KV k="Indexes" v={fmtInt(p.indexes)} />
        <KV k="Foreign keys" v={fmtInt(p.foreign_keys)} />
      </div>
    </Card>
  );
}

function EnvironmentCard({ payload }) {
  if (payload?.error) return <Card icon={Server} title="Environment" error={payload.error} />;
  const p = payload || {};
  const startedAt = p.started_at
    ? new Date(p.started_at).toLocaleString(undefined, {
        timeZone: "America/Chicago",
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "—";
  return (
    <Card icon={Server} title="Environment" source="/api/build-info · process">
      <KV k="Runtime" v={`Python ${p.python_version || "—"}`} />
      <KV k="Started at" v={startedAt} />
      <KV k="Uptime" v={fmtUptime(p.uptime_seconds)} />
      <KV k="Email service" v={p.postmark_enabled ? "live" : "dry-run"} />
      <KV k="From address" v={p.postmark_from_email || "—"} />
    </Card>
  );
}
